import * as React from "react";
import { Alert } from "@jhool-io/fe-components";
import {
    TREATMENT_REVIEW_NOTE_LABELS,
    TREATMENT_REVIEW_NOTE_SECTIONS,
    PSYCHOSOCIAL_ASSESSMENT_ALERT_TEXT,
} from "../../../../utils/constants";
import CheckIcon from "../../../../icons/Check";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import NoteContentItem from "../NoteContentItem/NoteContentItem";
import { cn, removeEnumUnderscore } from "../../../../utils/helpers";
import {
    FamilyTreatmentReviewNote,
    IAddendumDetails,
} from "../../../../modules/notes/types/notes.types";
import { AppointmentLocationTeletherapy } from "../../../../utils/types/session";
import useGetDiagnosisCodeToDisplay from "../../../../modules/notes/hooks";
import { useGetDiagnosisCodesDisplay } from "../../../../hooks/helpers";

interface ViewFamilyPsychosocialAssessmentProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewFamilyPsychosocialAssessment({
    noteContent,
    fieldAddendums,
}: ViewFamilyPsychosocialAssessmentProps) {
    const diagonosisCodes = useGetDiagnosisCodesDisplay(
        noteContent.diagnosis_codes
    );

    const isCheckboxChecked = (key: string[], checkboxValue: string) => {
        return Boolean(
            key?.find((value) => value.trim() === checkboxValue?.trim())
        );
    };

    const hasFamilyArray =
        noteContent?.family &&
        Array.isArray(noteContent.family) &&
        noteContent.family.length > 0;

    let familyMembers: FamilyTreatmentReviewNote[] = [];
    if (hasFamilyArray) {
        familyMembers = noteContent.family;
    } else if (noteContent) {
        familyMembers = [noteContent as FamilyTreatmentReviewNote];
    }

    return (
        <>
            <div className="mb-2">
                <Alert
                    description={PSYCHOSOCIAL_ASSESSMENT_ALERT_TEXT}
                    type="info"
                />
            </div>
            <p
                className={styles.sectionheader}
            >{`Family Count - ${familyMembers.length}`}</p>

            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.isClientPresent}
                fieldId="isClientPresent"
                addendums={fieldAddendums?.isClientPresent}
                content={
                    <div className={styles.checkbox}>
                        <span
                            className={cn(styles.checkbox_wrap, {
                                [styles.checked]: noteContent.isClientPresent,
                            })}
                        >
                            <CheckIcon />
                        </span>
                        {TREATMENT_REVIEW_NOTE_LABELS.isClientPresent}
                    </div>
                }
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.appointment_location}
                fieldId="appointment_location"
                addendums={fieldAddendums?.appointment_location}
                content={
                    removeEnumUnderscore(noteContent.appointment_location) ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.delivery_option}
                fieldId="delivery_option"
                addendums={fieldAddendums?.delivery_option}
                content={noteContent.delivery_option || "--"}
            />

            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.other_delivery_option}
                fieldId="other_delivery_option"
                addendums={fieldAddendums?.other_delivery_option}
                content={noteContent.other_delivery_option || "--"}
            />

            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.provider_location}
                fieldId="provider_location"
                addendums={fieldAddendums?.provider_location}
                content={noteContent.provider_location || "--"}
            />

            {noteContent.other_provider_location &&
            noteContent.provider_location ===
                AppointmentLocationTeletherapy.OTHER ? (
                <NoteContentItem
                    headerText={
                        TREATMENT_REVIEW_NOTE_LABELS.other_provider_location
                    }
                    fieldId="other_provider_location"
                    addendums={fieldAddendums?.other_provider_location}
                    content={noteContent.other_provider_location}
                />
            ) : null}
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.consent_was_received_for_the_teletherapy_session
                }
                fieldId="consent_was_received_for_the_teletherapy_session"
                addendums={
                    fieldAddendums?.consent_was_received_for_the_teletherapy_session
                }
                content={
                    <div className={styles.checkbox}>
                        <span
                            className={cn(styles.checkbox_wrap, {
                                [styles.checked]:
                                    noteContent.consent_was_received_for_the_teletherapy_session,
                            })}
                        >
                            {" "}
                            <CheckIcon />
                        </span>
                        {
                            TREATMENT_REVIEW_NOTE_LABELS.consent_was_received_for_the_teletherapy_session
                        }
                    </div>
                }
            />

            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.the_teletherapy_session_was_conducted_via_video
                }
                fieldId="the_teletherapy_session_was_conducted_via_video"
                addendums={
                    fieldAddendums?.the_teletherapy_session_was_conducted_via_video
                }
                content={
                    <div className={styles.checkbox}>
                        <span
                            className={cn(styles.checkbox_wrap, {
                                [styles.checked]:
                                    noteContent.the_teletherapy_session_was_conducted_via_video,
                            })}
                        >
                            <CheckIcon />
                        </span>
                        {
                            TREATMENT_REVIEW_NOTE_LABELS.the_teletherapy_session_was_conducted_via_video
                        }
                    </div>
                }
            />

            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.diagnosis_codes}
                fieldId="diagnosis_codes"
                addendums={fieldAddendums?.diagnosis_codes}
                content={
                    diagonosisCodes && Array.isArray(diagonosisCodes) ? (
                        <div className={styles.checkboxes}>
                            {diagonosisCodes.map((code) => (
                                <div className={styles.checkbox} key={code}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: true,
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    {code}
                                </div>
                            ))}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.secondary_diagnosis}
                fieldId="secondary_diagnosis"
                addendums={fieldAddendums?.secondary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.secondary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.tertiary_diagnosis}
                fieldId="tertiary_diagnosis"
                addendums={fieldAddendums?.tertiary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.tertiary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.additional_diagnosis_one
                }
                fieldId="additional_diagnosis_one"
                addendums={fieldAddendums?.additional_diagnosis_one}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_one
                )}
            />
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.additional_diagnosis_two
                }
                fieldId="additional_diagnosis_two"
                addendums={fieldAddendums?.additional_diagnosis_two}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_two
                )}
            />

            <p
                className={styles.sectionheader}
                id={TREATMENT_REVIEW_NOTE_SECTIONS.identity}
            >
                Identities
            </p>
            {familyMembers.map(
                (family: FamilyTreatmentReviewNote, i: number) => {
                    const familyId = hasFamilyArray
                        ? noteContent.family[i]?.family_id
                        : undefined;
                    const fieldIdSuffix = familyId ? `-${familyId}` : `-${i}`;
                    return (
                        <React.Fragment key={`${i + 1}`}>
                            <NoteContentItem
                                headerText={TREATMENT_REVIEW_NOTE_LABELS.name}
                                fieldId={`name${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[`name${fieldIdSuffix}`]
                                }
                                content={family.name || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.preferred_name
                                }
                                fieldId={`preferred_name${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `preferred_name${fieldIdSuffix}`
                                    ]
                                }
                                content={family.preferred_name || "--"}
                            />
                            <NoteContentItem
                                headerText={TREATMENT_REVIEW_NOTE_LABELS.gender}
                                fieldId={`gender${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[`gender${fieldIdSuffix}`]
                                }
                                content={family.gender || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.other_gender
                                }
                                fieldId={`other_gender${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `other_gender${fieldIdSuffix}`
                                    ]
                                }
                                content={family.other_gender || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.pronouns
                                }
                                fieldId={`pronouns${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[`pronouns${fieldIdSuffix}`]
                                }
                                content={family.pronouns || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.religion
                                }
                                fieldId={`religion${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[`religion${fieldIdSuffix}`]
                                }
                                content={family.religion || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.education
                                }
                                fieldId={`education${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `education${fieldIdSuffix}`
                                    ]
                                }
                                content={family.education || "--"}
                            />
                            <NoteContentItem
                                headerText={TREATMENT_REVIEW_NOTE_LABELS.race}
                                fieldId={`race${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[`race${fieldIdSuffix}`]
                                }
                                content={
                                    removeEnumUnderscore(family.race) || "--"
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.ethnicity
                                }
                                fieldId={`ethnicity${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `ethnicity${fieldIdSuffix}`
                                    ] ||
                                    fieldAddendums?.[
                                        `ethinicity${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.ethnicity ||
                                    (family as { ethinicity?: string })
                                        .ethinicity ||
                                    (!hasFamilyArray
                                        ? (
                                              noteContent as {
                                                  ethinicity?: string;
                                              }
                                          ).ethinicity
                                        : "") ||
                                    "--"
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.sexual_orientation
                                }
                                fieldId={`sexual_orientation${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `sexual_orientation${fieldIdSuffix}`
                                    ]
                                }
                                content={family.sexual_orientation || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.other_sexual_orientation
                                }
                                fieldId={`other_sexual_orientation${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `other_sexual_orientation${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.other_sexual_orientation || "--"
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.current_employment
                                }
                                fieldId={`current_employment${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `current_employment${fieldIdSuffix}`
                                    ]
                                }
                                content={family.current_employment || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.current_employment_details
                                }
                                fieldId={`current_employment_details${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `current_employment_details${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.current_employment_details || "--"
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.relationship_status
                                }
                                fieldId={`relationship_status${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `relationship_status${fieldIdSuffix}`
                                    ]
                                }
                                content={family.relationship_status || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.name_of_partner
                                }
                                fieldId={`name_of_partner${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `name_of_partner${fieldIdSuffix}`
                                    ]
                                }
                                content={family.name_of_partner || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.emergency_contact_information
                                }
                                fieldId={`emergency_contact_information${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `emergency_contact_information${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.emergency_contact_information || "--"
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.legal_problems
                                }
                                fieldId={`legal_problems${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `legal_problems${fieldIdSuffix}`
                                    ]
                                }
                                content={family.legal_problems || "--"}
                            />
                        </React.Fragment>
                    );
                }
            )}
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.what_are_the_problem_you_are_seeking_help_for
                }
                fieldId="what_are_the_problem_you_are_seeking_help_for"
                addendums={
                    fieldAddendums?.what_are_the_problem_you_are_seeking_help_for
                }
                content={
                    noteContent.what_are_the_problem_you_are_seeking_help_for ||
                    "--"
                }
            />

            <p
                className={styles.sectionheader}
                id={TREATMENT_REVIEW_NOTE_SECTIONS.symptoms}
            >
                Symptoms
            </p>
            {familyMembers.map(
                (family: FamilyTreatmentReviewNote, i: number) => {
                    const familyId = hasFamilyArray
                        ? noteContent.family[i]?.family_id
                        : undefined;
                    const fieldIdSuffix = familyId ? `-${familyId}` : `-${i}`;
                    return (
                        <React.Fragment key={`symptoms-${i + 1}`}>
                            <p className={styles.sectionheader}>
                                {i === 0 ? "Client" : `Collateral ${i}`}{" "}
                                {family.name ? `- ${family.name}` : ""}
                            </p>
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.anxiety
                                }
                                fieldId={`anxiety${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[`anxiety${fieldIdSuffix}`]
                                }
                                content={
                                    <div className={styles.items}>
                                        <div className={styles.checkbox}>
                                            <span
                                                className={cn(
                                                    styles.checkbox_wrap,
                                                    {
                                                        [styles.checked]:
                                                            family.anxiety,
                                                    }
                                                )}
                                            >
                                                <CheckIcon />
                                            </span>
                                            {
                                                TREATMENT_REVIEW_NOTE_LABELS.anxiety
                                            }
                                        </div>
                                    </div>
                                }
                            />
                            {family.anxiety && (
                                <>
                                    <NoteContentItem
                                        headerText={
                                            TREATMENT_REVIEW_NOTE_LABELS.anxiety_frequency
                                        }
                                        fieldId={`anxiety_frequency${fieldIdSuffix}`}
                                        addendums={
                                            fieldAddendums?.[
                                                `anxiety_frequency${fieldIdSuffix}`
                                            ]
                                        }
                                        content={
                                            family.anxiety_frequency || "--"
                                        }
                                    />
                                    <NoteContentItem
                                        headerText={
                                            TREATMENT_REVIEW_NOTE_LABELS.anxiety_details
                                        }
                                        fieldId={`anxiety_details${fieldIdSuffix}`}
                                        addendums={
                                            fieldAddendums?.[
                                                `anxiety_details${fieldIdSuffix}`
                                            ]
                                        }
                                        content={family.anxiety_details || "--"}
                                    />
                                </>
                            )}
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.panic_attacks
                                }
                                fieldId={`panic_attacks${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `panic_attacks${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    <div className={styles.items}>
                                        <div className={styles.checkbox}>
                                            <span
                                                className={cn(
                                                    styles.checkbox_wrap,
                                                    {
                                                        [styles.checked]:
                                                            family.panic_attacks,
                                                    }
                                                )}
                                            >
                                                <CheckIcon />
                                            </span>
                                            {
                                                TREATMENT_REVIEW_NOTE_LABELS.panic_attacks
                                            }
                                        </div>
                                    </div>
                                }
                            />
                            {family.panic_attacks && (
                                <>
                                    <NoteContentItem
                                        headerText={
                                            TREATMENT_REVIEW_NOTE_LABELS.panic_attacks_freqency
                                        }
                                        fieldId={`panic_attacks_freqency${fieldIdSuffix}`}
                                        addendums={
                                            fieldAddendums?.[
                                                `panic_attacks_freqency${fieldIdSuffix}`
                                            ]
                                        }
                                        content={
                                            family.panic_attacks_freqency ||
                                            (!hasFamilyArray
                                                ? noteContent.panic_attacks_freqency
                                                : "") ||
                                            "--"
                                        }
                                    />
                                    <NoteContentItem
                                        headerText={
                                            TREATMENT_REVIEW_NOTE_LABELS.panic_attacks_details
                                        }
                                        fieldId={`panic_attacks_details${fieldIdSuffix}`}
                                        addendums={
                                            fieldAddendums?.[
                                                `panic_attacks_details${fieldIdSuffix}`
                                            ]
                                        }
                                        content={
                                            family.panic_attacks_details || "--"
                                        }
                                    />
                                </>
                            )}
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.depression
                                }
                                fieldId={`depression${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `depression${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    <div className={styles.items}>
                                        <div className={styles.checkbox}>
                                            <span
                                                className={cn(
                                                    styles.checkbox_wrap,
                                                    {
                                                        [styles.checked]:
                                                            family.depression,
                                                    }
                                                )}
                                            >
                                                <CheckIcon />
                                            </span>
                                            {
                                                TREATMENT_REVIEW_NOTE_LABELS.depression
                                            }
                                        </div>
                                    </div>
                                }
                            />
                            {family.depression && (
                                <>
                                    <NoteContentItem
                                        headerText={
                                            TREATMENT_REVIEW_NOTE_LABELS.depression_freqency
                                        }
                                        fieldId={`depression_freqency${fieldIdSuffix}`}
                                        addendums={
                                            fieldAddendums?.[
                                                `depression_frequency${fieldIdSuffix}`
                                            ] ||
                                            fieldAddendums?.[
                                                `depression_freqency${fieldIdSuffix}`
                                            ]
                                        }
                                        content={
                                            family.depression_freqency ||
                                            (!hasFamilyArray
                                                ? noteContent.depression_freqency
                                                : "") ||
                                            "--"
                                        }
                                    />
                                    <NoteContentItem
                                        headerText={
                                            TREATMENT_REVIEW_NOTE_LABELS.depression_details
                                        }
                                        fieldId={`depression_details${fieldIdSuffix}`}
                                        addendums={
                                            fieldAddendums?.[
                                                `depression_details${fieldIdSuffix}`
                                            ]
                                        }
                                        content={
                                            family.depression_details || "--"
                                        }
                                    />
                                </>
                            )}
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.selected_symptoms
                                }
                                fieldId={`selected_symptoms${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `selected_symptoms${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family?.selected_symptoms?.length > 0 ? (
                                        <div className={styles.checkboxes}>
                                            {isCheckboxChecked(
                                                family?.selected_symptoms,
                                                "Easily distracted"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family?.selected_symptoms,
                                                                        "Easily distracted"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Easily distracted
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family?.selected_symptoms,
                                                "Impulsive"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family?.selected_symptoms,
                                                                        "Impulsive"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Impulsive
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Paranoia"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Paranoia"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Paranoia
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Alcohol"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Alcohol"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Alcohol
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Anger outburst"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Anger outburst"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Anger outburst
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Unable to feel pleasure"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Unable to feel pleasure"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Unable to feel pleasure
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Excessive energy"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Excessive energy"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Excessive energy
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Recreational drug use"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Recreational drug use"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Recreational drug use
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Tobacco"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Tobacco"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Tobacco
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family.selected_symptoms,
                                                "Racing thoughts"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family.selected_symptoms,
                                                                        "Racing thoughts"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Racing thoughts
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        "--"
                                    )
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.other_symptoms
                                }
                                fieldId={`other_symptoms${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `other_symptoms${fieldIdSuffix}`
                                    ]
                                }
                                content={family.other_symptoms || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.physical_symptoms
                                }
                                fieldId={`physical_symptoms${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `physical_symptoms${fieldIdSuffix}`
                                    ]
                                }
                                content={family.physical_symptoms || "--"}
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.medication_physical_psychiatric
                                }
                                fieldId={`medication_physical_psychiatric${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `medication_physical_psychiatric${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.medication_physical_psychiatric ||
                                    "--"
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.sleeping_habits_and_concerns
                                }
                                fieldId={`sleeping_habits_and_concerns${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `sleeping_habits_and_concerns${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.sleeping_habits_and_concerns || "--"
                                }
                            />
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.difficulties_with_appetite_or_eating_patterns
                                }
                                fieldId={`difficulties_with_appetite_or_eating_patterns${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `difficulties_with_appetite_or_eating_patterns${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.difficulties_with_appetite_or_eating_patterns ||
                                    "--"
                                }
                            />
                        </React.Fragment>
                    );
                }
            )}

            <p
                className={styles.sectionheader}
                id={TREATMENT_REVIEW_NOTE_SECTIONS["suicide risk assessment"]}
            >
                Suicide risk assessment
            </p>
            {familyMembers.map(
                (family: FamilyTreatmentReviewNote, i: number) => {
                    const familyId = hasFamilyArray
                        ? noteContent.family[i]?.family_id
                        : undefined;
                    const fieldIdSuffix = familyId ? `-${familyId}` : `-${i}`;
                    return (
                        <React.Fragment key={`suicide-${i + 1}`}>
                            <p className={styles.sectionheader}>
                                {i === 0 ? "Client" : `Collateral ${i}`}{" "}
                                {family.name ? `- ${family.name}` : ""}
                            </p>
                            <NoteContentItem
                                headerText={`1. ${TREATMENT_REVIEW_NOTE_LABELS.been_so_distressed_you_seriously_wished_to_end_your_life}`}
                                fieldId={`been_so_distressed_you_seriously_wished_to_end_your_life${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `been_so_distressed_you_seriously_wished_to_end_your_life${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.been_so_distressed_you_seriously_wished_to_end_your_life ||
                                    "--"
                                }
                            />
                            {(family.been_so_distressed_you_seriously_wished_to_end_your_life ===
                                "Today" ||
                                family.been_so_distressed_you_seriously_wished_to_end_your_life ===
                                    "Recently") && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                    }
                                    fieldId={`describe_been_so_distressed_you_seriously_wished_to_end_your_life${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_been_so_distressed_you_seriously_wished_to_end_your_life${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_been_so_distressed_you_seriously_wished_to_end_your_life ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`2. ${TREATMENT_REVIEW_NOTE_LABELS.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live}`}
                                fieldId={`has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ||
                                    "--"
                                }
                            />
                            {family.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                    }
                                    fieldId={`describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`3. ${TREATMENT_REVIEW_NOTE_LABELS.a_specific_plan_of_how_you_would_kill_yourself}`}
                                fieldId={`a_specific_plan_of_how_you_would_kill_yourself${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `a_specific_plan_of_how_you_would_kill_yourself${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.a_specific_plan_of_how_you_would_kill_yourself ||
                                    "--"
                                }
                            />
                            {family.a_specific_plan_of_how_you_would_kill_yourself ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_a_specific_plan_of_how_you_would_kill_yourself
                                    }
                                    fieldId={`describe_a_specific_plan_of_how_you_would_kill_yourself${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_a_specific_plan_of_how_you_would_kill_yourself${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_a_specific_plan_of_how_you_would_kill_yourself ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`4. ${TREATMENT_REVIEW_NOTE_LABELS.access_to_weapons_or_means_of_hurting_self}`}
                                fieldId={`access_to_weapons_or_means_of_hurting_self${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `access_to_weapons_or_means_of_hurting_self${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.access_to_weapons_or_means_of_hurting_self ||
                                    "--"
                                }
                            />
                            {family.access_to_weapons_or_means_of_hurting_self ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_access_to_weapons_or_means_of_hurting_self
                                    }
                                    fieldId={`describe_access_to_weapons_or_means_of_hurting_self${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_access_to_weapons_or_means_of_hurting_self${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_access_to_weapons_or_means_of_hurting_self ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`5. ${TREATMENT_REVIEW_NOTE_LABELS.made_serious_suicide_attempts}`}
                                fieldId={`made_serious_suicide_attempts${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `made_serious_suicide_attempts${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.made_serious_suicide_attempts || "--"
                                }
                            />
                            {family.made_serious_suicide_attempts === "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_made_serious_suicide_attempts
                                    }
                                    fieldId={`describe_made_serious_suicide_attempts${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_made_serious_suicide_attempts${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_made_serious_suicide_attempts ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`6. ${TREATMENT_REVIEW_NOTE_LABELS.purposely_done_something_to_hurt_yourself}`}
                                fieldId={`purposely_done_something_to_hurt_yourself${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `purposely_done_something_to_hurt_yourself${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.purposely_done_something_to_hurt_yourself ||
                                    "--"
                                }
                            />
                            {family.purposely_done_something_to_hurt_yourself ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_done_something_to_hurt_yourself
                                    }
                                    fieldId={`describe_done_something_to_hurt_yourself${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_done_something_to_hurt_yourself${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_done_something_to_hurt_yourself ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`7. ${TREATMENT_REVIEW_NOTE_LABELS.heard_voices_telling_you_to_hurt_yourself}`}
                                fieldId={`heard_voices_telling_you_to_hurt_yourself${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `heard_voices_telling_you_to_hurt_yourself${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.heard_voices_telling_you_to_hurt_yourself ||
                                    "--"
                                }
                            />
                            {family.heard_voices_telling_you_to_hurt_yourself ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_heard_voices_telling_you_to_hurt_yourself
                                    }
                                    fieldId={`describe_heard_voices_telling_you_to_hurt_yourself${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_heard_voices_telling_you_to_hurt_yourself${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_heard_voices_telling_you_to_hurt_yourself ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`8. ${TREATMENT_REVIEW_NOTE_LABELS.had_relatives_who_attempted_or_commited_sucide}`}
                                fieldId={`had_relatives_who_attempted_or_commited_sucide${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `had_relatives_who_attempted_or_commited_sucide${fieldIdSuffix}`
                                    ] ||
                                    fieldAddendums?.[
                                        `had_relatives_who_attempted_or_commited_sucide${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.had_relatives_who_attempted_or_commited_sucide ||
                                    (!hasFamilyArray
                                        ? noteContent.had_relatives_who_attempted_or_commited_sucide
                                        : "") ||
                                    "--"
                                }
                            />
                            {family.had_relatives_who_attempted_or_commited_sucide ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_had_relatives_who_attempted_or_commited_sucide
                                    }
                                    fieldId={`describe_had_relatives_who_attempted_or_commited_sucide${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_had_relatives_who_attempted_or_commited_sucide${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_had_relatives_who_attempted_or_commited_sucide ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`9. ${TREATMENT_REVIEW_NOTE_LABELS.had_thoughts_of_killing_or_seriously_hurting_someone}`}
                                fieldId={`had_thoughts_of_killing_or_seriously_hurting_someone${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `had_thoughts_of_killing_or_seriously_hurting_someone${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.had_thoughts_of_killing_or_seriously_hurting_someone ||
                                    "--"
                                }
                            />
                            {family.had_thoughts_of_killing_or_seriously_hurting_someone ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                    }
                                    fieldId={`describe_had_thoughts_of_killing_or_seriously_hurting_someone${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_had_thoughts_of_killing_or_seriously_hurting_someone${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_had_thoughts_of_killing_or_seriously_hurting_someone ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`10. ${TREATMENT_REVIEW_NOTE_LABELS.heard_voices_telling_you_to_hurt_others}`}
                                fieldId={`heard_voices_telling_you_to_hurt_others${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `heard_voices_telling_you_to_hurt_others${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.heard_voices_telling_you_to_hurt_others ||
                                    "--"
                                }
                            />
                            {family.heard_voices_telling_you_to_hurt_others ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_heard_voices_telling_you_to_hurt_others
                                    }
                                    fieldId={`describe_heard_voices_telling_you_to_hurt_others${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_heard_voices_telling_you_to_hurt_others${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_heard_voices_telling_you_to_hurt_others ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`11. ${TREATMENT_REVIEW_NOTE_LABELS.hurt_someone_or_destroyed_property_on_purpose}`}
                                fieldId={`hurt_someone_or_destroyed_property_on_purpose${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `hurt_someone_or_destroyed_property_on_purpose${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.hurt_someone_or_destroyed_property_on_purpose ||
                                    "--"
                                }
                            />
                            {family.hurt_someone_or_destroyed_property_on_purpose ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_hurt_someone_or_destroyed_property_on_purpose
                                    }
                                    fieldId={`describe_hurt_someone_or_destroyed_property_on_purpose${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_hurt_someone_or_destroyed_property_on_purpose${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_hurt_someone_or_destroyed_property_on_purpose ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`12. ${TREATMENT_REVIEW_NOTE_LABELS.slapped_kicked_punched_someone_with_intent_to_harm}`}
                                fieldId={`slapped_kicked_punched_someone_with_intent_to_harm${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `slapped_kicked_punched_someone_with_intent_to_harm${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.slapped_kicked_punched_someone_with_intent_to_harm ||
                                    "--"
                                }
                            />
                            {family.slapped_kicked_punched_someone_with_intent_to_harm ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                    }
                                    fieldId={`describe_slapped_kicked_punched_someone_with_intent_to_harm${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_slapped_kicked_punched_someone_with_intent_to_harm${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_slapped_kicked_punched_someone_with_intent_to_harm ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`13. ${TREATMENT_REVIEW_NOTE_LABELS.been_arrested_or_detained_for_violent_behavior}`}
                                fieldId={`been_arrested_or_detained_for_violent_behavior${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `been_arrested_or_detained_for_violent_behavior${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.been_arrested_or_detained_for_violent_behavior ||
                                    "--"
                                }
                            />
                            {family.been_arrested_or_detained_for_violent_behavior ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_been_arrested_or_detained_for_violent_behavior
                                    }
                                    fieldId={`describe_been_arrested_or_detained_for_violent_behavior${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_been_arrested_or_detained_for_violent_behavior${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_been_arrested_or_detained_for_violent_behavior ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`14. ${TREATMENT_REVIEW_NOTE_LABELS.been_to_jail_for_any_reason}`}
                                fieldId={`been_to_jail_for_any_reason${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `been_to_jail_for_any_reason${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.been_to_jail_for_any_reason || "--"
                                }
                            />
                            {family.been_to_jail_for_any_reason === "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_been_to_jail_for_any_reason
                                    }
                                    fieldId={`describe_been_to_jail_for_any_reason${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_been_to_jail_for_any_reason${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_been_to_jail_for_any_reason ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`15. ${TREATMENT_REVIEW_NOTE_LABELS.been_on_probation_for_any_reason}`}
                                fieldId={`been_on_probation_for_any_reason${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `been_on_probation_for_any_reason${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.been_on_probation_for_any_reason ||
                                    "--"
                                }
                            />
                            {family.been_on_probation_for_any_reason ===
                                "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_been_on_probation_for_any_reason
                                    }
                                    fieldId={`describe_been_on_probation_for_any_reason${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_been_on_probation_for_any_reason${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_been_on_probation_for_any_reason ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={`16. ${TREATMENT_REVIEW_NOTE_LABELS.do_you_have_access_to_guns}`}
                                fieldId={`do_you_have_access_to_guns${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `do_you_have_access_to_guns${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family.do_you_have_access_to_guns || "--"
                                }
                            />
                            {family.do_you_have_access_to_guns === "Yes" && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.describe_do_you_have_access_to_guns
                                    }
                                    fieldId={`describe_do_you_have_access_to_guns${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `describe_do_you_have_access_to_guns${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.describe_do_you_have_access_to_guns ||
                                        "--"
                                    }
                                />
                            )}
                            <NoteContentItem
                                headerText={
                                    TREATMENT_REVIEW_NOTE_LABELS.patients_ability_and_capability_to_respond_to_treatment
                                }
                                fieldId={`patients_ability_and_capability_to_respond_to_treatment${fieldIdSuffix}`}
                                addendums={
                                    fieldAddendums?.[
                                        `patients_ability_and_capability_to_respond_to_treatment${fieldIdSuffix}`
                                    ]
                                }
                                content={
                                    family
                                        ?.patients_ability_and_capability_to_respond_to_treatment
                                        ?.length > 0 ? (
                                        <div className={styles.checkboxes}>
                                            {isCheckboxChecked(
                                                family?.patients_ability_and_capability_to_respond_to_treatment,
                                                "Consistent, good-quality patient-oriented evidence"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family?.patients_ability_and_capability_to_respond_to_treatment,
                                                                        "Consistent, good-quality patient-oriented evidence"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Consistent, good-quality
                                                    patient-oriented evidence
                                                </div>
                                            )}
                                            {isCheckboxChecked(
                                                family?.patients_ability_and_capability_to_respond_to_treatment,
                                                "Diminished/poor or limited-quality patient-oriented evidence"
                                            ) && (
                                                <div
                                                    className={styles.checkbox}
                                                >
                                                    <span
                                                        className={cn(
                                                            styles.checkbox_wrap,
                                                            {
                                                                [styles.checked]:
                                                                    isCheckboxChecked(
                                                                        family?.patients_ability_and_capability_to_respond_to_treatment,
                                                                        "Diminished/poor or limited-quality patient-oriented evidence"
                                                                    ),
                                                            }
                                                        )}
                                                    >
                                                        <CheckIcon />
                                                    </span>
                                                    Diminished/poor or
                                                    limited-quality
                                                    patient-oriented evidence
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        "--"
                                    )
                                }
                            />
                            {family?.patients_ability_and_capability_to_respond_to_treatment?.includes(
                                "Diminished/poor or limited-quality patient-oriented evidence"
                            ) && (
                                <NoteContentItem
                                    headerText={
                                        TREATMENT_REVIEW_NOTE_LABELS.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                    }
                                    fieldId={`details_for_diminished_poor_limited_quality_patient_oriented_evidence${fieldIdSuffix}`}
                                    addendums={
                                        fieldAddendums?.[
                                            `details_for_diminished_poor_limited_quality_patient_oriented_evidence${fieldIdSuffix}`
                                        ]
                                    }
                                    content={
                                        family.details_for_diminished_poor_limited_quality_patient_oriented_evidence ||
                                        "--"
                                    }
                                />
                            )}
                        </React.Fragment>
                    );
                }
            )}

            <p
                className={styles.sectionheader}
                id={TREATMENT_REVIEW_NOTE_SECTIONS.summary}
            >
                Summary
            </p>
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.people_present_at_session
                }
                fieldId="people_present_at_session"
                addendums={fieldAddendums?.people_present_at_session}
                content={
                    noteContent?.people_present_at_session?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Patient"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Patient"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Patient
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Mother"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Mother"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Mother
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Stepfather"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Stepfather"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Stepfather
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Spouse"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Spouse"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Spouse
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Father"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Father"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Father
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Stepmother"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Stepmother"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Stepmother
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Partner"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Partner"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Partner
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Guardian"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Guardian"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Guardian
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.people_present_at_session,
                                "Other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.people_present_at_session,
                                                "Other"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Other
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.other_person_present_at_session
                }
                fieldId="other_person_present_at_session"
                addendums={fieldAddendums?.other_person_present_at_session}
                content={noteContent.other_person_present_at_session || "--"}
            />
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.type_of_treatment_recommended
                }
                fieldId="type_of_treatment_recommended"
                addendums={fieldAddendums?.type_of_treatment_recommended}
                content={
                    noteContent?.type_of_treatment_recommended?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                noteContent?.type_of_treatment_recommended,
                                "Individual"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.type_of_treatment_recommended,
                                                "Individual"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Individual
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.type_of_treatment_recommended,
                                "Family"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.type_of_treatment_recommended,
                                                "Family"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Family
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.type_of_treatment_recommended,
                                "Group"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.type_of_treatment_recommended,
                                                "Group"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Group
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.type_of_treatment_recommended,
                                "Collateral/Parenting"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.type_of_treatment_recommended,
                                                "Collateral/Parenting"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Collateral/Parenting
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.treatment_modality_intervention
                }
                fieldId="treatment_modality_intervention"
                addendums={fieldAddendums?.treatment_modality_intervention}
                content={
                    noteContent?.treatment_modality_intervention?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Cognitive/Behavioral"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Cognitive/Behavioral"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Cognitive/Behavioral
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Behavioral Modification"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Behavioral Modification"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Behavioral Modification
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Supportive"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Supportive"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Supportive
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Marital/Couples Therapy"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Marital/Couples Therapy"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Marital/Couples Therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Family Therapy"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Family Therapy"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Family Therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Stress Management"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Stress Management"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Stress Management
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Psychodynamic"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Psychodynamic"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Psychodynamic
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Parent Training"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Parent Training"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Parent Training
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Crisis Intervention"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Crisis Intervention"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Crisis Intervention
                                </div>
                            )}
                            {isCheckboxChecked(
                                noteContent?.treatment_modality_intervention,
                                "Other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: isCheckboxChecked(
                                                noteContent?.treatment_modality_intervention,
                                                "Other"
                                            ),
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Other
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    TREATMENT_REVIEW_NOTE_LABELS.other_treatment_modality_intervention
                }
                fieldId="other_treatment_modality_intervention"
                addendums={
                    fieldAddendums?.other_treatment_modality_intervention
                }
                content={
                    noteContent.other_treatment_modality_intervention || "--"
                }
            />
            <p
                className={styles.sectionheader}
                id={TREATMENT_REVIEW_NOTE_SECTIONS["treatment goals"]}
            >
                Treatment goals
            </p>
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.treatment_goal_one}
                fieldId="treatment_goal_one"
                addendums={fieldAddendums?.treatment_goal_one}
                content={noteContent.treatment_goal_one || "--"}
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.treatment_goal_two}
                fieldId="treatment_goal_two"
                addendums={fieldAddendums?.treatment_goal_two}
                content={noteContent.treatment_goal_two || "--"}
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.treatment_goal_three}
                fieldId="treatment_goal_three"
                addendums={fieldAddendums?.treatment_goal_three}
                content={noteContent.treatment_goal_three || "--"}
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.medication_updates}
                fieldId="medication_updates"
                addendums={fieldAddendums?.medication_updates}
                content={noteContent.medication_updates || "--"}
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.frequency_of_sessions}
                fieldId="frequency_of_sessions"
                addendums={fieldAddendums?.frequency_of_sessions}
                content={noteContent.frequency_of_sessions || "--"}
            />
            {noteContent.other_frequency_of_sessions && (
                <NoteContentItem
                    headerText={
                        TREATMENT_REVIEW_NOTE_LABELS.other_frequency_of_sessions
                    }
                    fieldId="other_frequency_of_sessions"
                    addendums={fieldAddendums?.other_frequency_of_sessions}
                    content={noteContent.other_frequency_of_sessions || "--"}
                />
            )}
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.prognosis}
                fieldId="prognosis"
                addendums={fieldAddendums?.prognosis}
                content={noteContent.prognosis || "--"}
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.subjective_report}
                fieldId="subjective_report"
                addendums={fieldAddendums?.subjective_report}
                content={noteContent.subjective_report || "--"}
            />
            <NoteContentItem
                headerText={TREATMENT_REVIEW_NOTE_LABELS.summary}
                fieldId="summary"
                addendums={fieldAddendums?.summary}
                content={noteContent.summary || "--"}
            />
        </>
    );
}
